<template>
  <div>
    <el-row>
      <el-col>
        <el-card shadow="never">
          <el-table
            v-loading="isLoading"
            :data="rules"
            stripe
          >
            <el-table-column
              label="Name"
              prop="name"
              min-width="180"
              show-overflow-tooltip
              :formatter="(row, column, value) => value || '-'"
            />
            <el-table-column
              label="Expression"
              prop="expression"
              min-width="280"
              show-overflow-tooltip
              :formatter="(row, column, value) => value || '-'"
            />
            <el-table-column
              label="Include Child Accounts"
              prop="allow_children"
              width="180"
              :formatter="(row, column, value) => value ? 'Yes' : 'No'"
            />
            <el-table-column
              label="Global"
              prop="global"
              width="100"
              :formatter="(row, column, value) => value ? 'Yes' : 'No'"
            />
            <el-table-column
              label="Services"
              min-width="320"
            >
              <template slot-scope="scope">
                <span v-if="!scope.row.services.length">-</span>
                <el-tag
                  v-for="linkedService in scope.row.services"
                  :key="linkedService.id"
                  size="mini"
                  class="service-tag"
                >
                  {{ serviceLabel(linkedService.carrier_service) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              label="Actions"
              align="right"
              width="180"
            >
              <template slot-scope="scope">
                <el-button
                  size="mini"
                  @click.native="editRule(scope.row)"
                >
                  Edit
                </el-button>
                <el-button
                  size="mini"
                  type="danger"
                  @click.native="deleteRule(scope.row)"
                >
                  Delete
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <br>
          <div style="text-align: center;">
            <el-pagination
              :current-page.sync="page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size.sync="perPage"
              layout="total, sizes, prev, pager, next, jumper"
              :total="total"
              @size-change="loadRules"
              @current-change="loadRules"
            />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog
      v-if="isDialogVisible"
      :title="rule.exists ? 'Edit Consignment Rule' : 'Create Consignment Rule'"
      :visible.sync="isDialogVisible"
      :close-on-click-modal="false"
      width="680px"
      @closed="closeDialog"
    >
      <el-form
        ref="ruleForm"
        v-loading="isSaving"
        :model="form"
        :rules="formRules"
        label-position="top"
        size="small"
      >
        <el-form-item
          label="Name"
          prop="name"
        >
          <el-input
            v-model="form.name"
            placeholder="Enter a name for this rule"
            clearable
          />
        </el-form-item>

        <el-form-item
          label="Expression"
          prop="expression"
        >
          <el-input
            v-model="form.expression"
            type="textarea"
            :rows="5"
            placeholder="Enter the rule expression"
          />
        </el-form-item>

        <el-form-item
          label="Services"
          prop="serviceIds"
        >
          <el-select
            v-model="form.serviceIds"
            v-loading="isLoadingServices"
            multiple
            filterable
            collapse-tags
            class="w-100"
            placeholder="Select one or more services"
          >
            <el-option
              v-for="service in services"
              :key="service.id"
              :label="serviceLabel(service)"
              :value="service.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-checkbox
            v-model="form.global"
            @change="validateServices"
          >
            Make this rule global
          </el-checkbox>
        </el-form-item>

        <el-form-item>
          <el-checkbox v-model="form.allowChildren">
            Apply this rule to child accounts
          </el-checkbox>
        </el-form-item>
      </el-form>

      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button @click.native="isDialogVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          :loading="isSaving"
          @click.native="saveRule"
        >
          {{ rule.exists ? 'Save' : 'Create' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data () {
    return {
      accountId: this.$route.params.accountId,
      rules: [],
      services: [],
      rule: null,
      form: {
        name: '',
        expression: '',
        global: false,
        allowChildren: false,
        serviceIds: []
      },
      formRules: {
        name: [
          { required: true, message: 'Name is required', trigger: 'blur' }
        ],
        serviceIds: [
          {
            validator: (rule, value, callback) => {
              if (!this.form.global && !value.length) {
                callback(new Error('Select at least one service'))
                return
              }

              callback()
            },
            trigger: 'change'
          }
        ]
      },
      page: 1,
      perPage: 20,
      total: 0,
      isLoading: false,
      isLoadingServices: false,
      isSaving: false,
      isDialogVisible: false,
      actions: [
        {
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => this.createRule()
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.loadRules()
        }
      ],
      breadcrumb: {
        name: 'Consignment Rules',
        route: { name: 'manage-accounts-account-configurations-rules', params: { accountId: this.$route.params.accountId } }
      }
    }
  },
  created () {
    this.loadRules()
  },
  methods: {
    loadRules () {
      this.isLoading = true
      return this.$sdk.account_consignment_rule.list(builder => {
        builder.context = { resource: 'account', id: this.accountId }
        builder.include = ['services.carrier_service.carrier']
        builder.page = this.page
        builder.limit = this.perPage
        builder.order = 'created_at:desc'
      })
        .then(response => {
          this.rules = response
          this.total = response.total
        })
        .finally(() => { this.isLoading = false })
    },
    loadServices () {
      this.isLoadingServices = true
      return this.$sdk.service.list(builder => {
        builder.include = ['carrier']
        builder.filters = { account_carrier_service_setting_strict_account: [this.accountId] }
        builder.page = 1
        builder.limit = 1000
        builder.order = 'service_name:asc'
      })
        .then(response => {
          this.services = response.sort((first, second) => this.serviceLabel(first).localeCompare(this.serviceLabel(second)))
          const availableServiceIds = this.services.map(service => service.id)
          this.form.serviceIds = this.form.serviceIds.filter(serviceId => availableServiceIds.includes(serviceId))
        })
        .finally(() => { this.isLoadingServices = false })
    },
    createRule () {
      this.rule = new this.$sdk.account_consignment_rule()
      this.resetForm()
      this.openDialog()
    },
    editRule (rule) {
      this.rule = rule
      this.form = {
        name: rule.name || '',
        expression: rule.expression || '',
        global: Boolean(rule.global),
        allowChildren: Boolean(rule.allow_children),
        serviceIds: rule.services
          .map(linkedService => linkedService.carrier_service && linkedService.carrier_service.id)
          .filter(Boolean)
      }
      this.openDialog()
    },
    openDialog () {
      this.isDialogVisible = true
      this.loadServices()
    },
    closeDialog () {
      this.rule = null
      this.services = []
      this.resetForm()
    },
    resetForm () {
      this.form = {
        name: '',
        expression: '',
        global: false,
        allowChildren: false,
        serviceIds: []
      }
    },
    validateServices () {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.validateField('serviceIds')
      }
    },
    saveRule () {
      this.$refs.ruleForm.validate(valid => {
        if (!valid) return

        this.isSaving = true
        const existingLinks = this.rule.services ? this.rule.services.slice() : []

        this.rule.name = this.form.name
        this.rule.expression = this.form.expression || null
        this.rule.global = this.form.global
        this.rule.allow_children = this.form.allowChildren

        this.rule.save(builder => {
          builder.context = { resource: 'account', id: this.accountId }
        })
          .then(() => this.syncServices(existingLinks))
          .then(() => {
            this.$message({ message: 'Successfully saved', type: 'success' })
            this.isDialogVisible = false
            return this.loadRules()
          })
          .finally(() => { this.isSaving = false })
      })
    },
    syncServices (existingLinks) {
      const selectedIds = this.form.serviceIds
      const linkedIds = existingLinks
        .map(link => link.carrier_service && link.carrier_service.id)
        .filter(Boolean)
      const linksToDelete = existingLinks.filter(link => {
        return link.carrier_service && !selectedIds.includes(link.carrier_service.id)
      })
      const servicesToCreate = this.services.filter(service => {
        return selectedIds.includes(service.id) && !linkedIds.includes(service.id)
      })

      return Promise.all([
        ...linksToDelete.map(link => link.delete(builder => {
          builder.context = [
            this.rule,
            { resource: 'account', id: this.accountId }
          ]
        })),
        ...servicesToCreate.map(service => {
          const link = new this.$sdk.account_consignment_rule_service()
          link.rule = this.rule
          link.carrier_service = service
          return link.create(builder => {
            builder.context = [
              this.rule,
              { resource: 'account', id: this.accountId }
            ]
          })
        })
      ])
    },
    deleteRule (rule) {
      this.$confirm('Are you sure you want to delete this consignment rule?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.isLoading = true
        return rule.delete(builder => {
          builder.context = { resource: 'account', id: this.accountId }
        })
          .then(() => this.$message({ message: 'Successfully deleted', type: 'success' }))
          .finally(() => this.loadRules())
      }).catch(() => {})
    },
    serviceLabel (service) {
      if (!service) return '-'

      const carrierName = service.carrier && service.carrier.name
      return [carrierName, service.name, service.chainit].filter(Boolean).join(' - ')
    }
  }
}
</script>

<style lang="scss" scoped>
.service-tag {
  margin: 2px 4px 2px 0;
}
</style>
